import { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import { getCaresShellBootstrap } from '../shell/bootstrap';
import {
  fetchMyScheduleRequestPolicy,
  type RequestPolicyAction,
  type RequestPolicyItem,
  type RequestPolicyResponse,
} from './request-policy-api';

const completenessTone: Record<string, string> = {
  complete: 'success',
  partial: 'warning',
  unavailable: 'danger',
};

const statusTone: Record<string, string> = {
  resolved: 'success',
  partial: 'warning',
  unavailable: 'secondary',
  conservative_unavailable: 'warning',
};

const actionLabels: Array<{ key: keyof RequestPolicyResponse['actionPermissions']; label: string }> = [
  { key: 'submit', label: 'Submit requests' },
  { key: 'modify', label: 'Modify requests' },
  { key: 'delete', label: 'Delete requests' },
];

const PolicySectionCard: FC<{ title: string; items: RequestPolicyItem[]; emptyMessage: string }> = ({
  title,
  items,
  emptyMessage,
}) => (
  <Card className="custom-card h-100">
    <Card.Header>
      <Card.Title>{title}</Card.Title>
    </Card.Header>
    <Card.Body>
      {items.length === 0 ? (
        <Alert variant="light" className="mb-0 border">
          {emptyMessage}
        </Alert>
      ) : (
        <div className="d-flex flex-column gap-3">
          {items.map((item) => (
            <div key={item.key} className="border rounded p-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <h5 className="mb-0">{item.title}</h5>
                <Badge bg="" className={`bg-${statusTone[item.status] ?? 'secondary'}-transparent text-${statusTone[item.status] ?? 'secondary'}`}>
                  {item.status.replace('_', ' ')}
                </Badge>
              </div>
              <p className="mb-2">{item.summary}</p>
              <p className="text-muted mb-0">{item.detail}</p>
              <div className="text-muted fs-12 mt-2">
                Source: {item.provenance}
                {item.effectiveDate ? ` · Effective ${new Date(item.effectiveDate).toLocaleDateString()}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card.Body>
  </Card>
);

const ActionPermissionCard: FC<{ label: string; action: RequestPolicyAction }> = ({ label, action }) => {
  const tone = statusTone[action.status] ?? 'secondary';

  return (
    <Card className="custom-card h-100">
      <Card.Body>
        <p className="text-muted text-uppercase fs-12 fw-semibold mb-2">{label}</p>
        <div className={`text-${action.allowed ? 'success' : tone} fs-4 fw-semibold mb-2`}>
          {action.allowed ? 'Allowed' : 'Not granted'}
        </div>
        <p className="text-muted mb-0">{action.message}</p>
      </Card.Body>
    </Card>
  );
};

const RequestPolicyPage: FC = () => {
  const bootstrap = getCaresShellBootstrap();
  const [policy, setPolicy] = useState<RequestPolicyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>('');

  const loadPolicy = async (group?: string) => {
    setLoading(true);
    setError(null);

    try {
      const payload = await fetchMyScheduleRequestPolicy(group);
      setPolicy(payload);
      setSelectedGroup(payload.availableGroups.find((item) => item.isSelected)?.slug ?? '');
    } catch (requestError) {
      setPolicy(null);
      setError(requestError instanceof Error ? requestError.message : 'Unable to load request policy.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPolicy();
  }, []);

  const selectedGroupLabel = useMemo(() => {
    const activeGroup = policy?.availableGroups.find((group) => group.isSelected);
    if (!activeGroup) return 'No active department scope';

    return `${activeGroup.name} (${activeGroup.company.slug ?? 'company'})`;
  }, [policy]);

  if (!bootstrap.authenticated) {
    return (
      <>
        <CaresPageHeader homepage="My Schedule" activepage="My Requests" page="Request Policy" actions={<></>} />
        <Alert variant="danger" className="custom-card">
          This policy reference requires an authenticated CARES session.
          <Link to="/login" className="alert-link ms-1">
            Sign in
          </Link>
          to review resolved request policy details.
        </Alert>
      </>
    );
  }

  return (
    <>
      <CaresPageHeader homepage="My Schedule" activepage="My Requests" page="Request Policy" actions={<></>} />

      <Card className="custom-card cares-page-hero">
        <Card.Body className="p-3 p-xl-4">
          <div className="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3">
            <div>
              <Badge bg="" className={`bg-${completenessTone[policy?.dataCompleteness.status ?? 'partial']}-transparent text-${completenessTone[policy?.dataCompleteness.status ?? 'partial']}`}>
                Runtime-backed policy reference
              </Badge>
              <h2 className="mt-3 mb-2">Request Policy</h2>
              <p className="text-muted mb-0 fs-15" style={{ maxWidth: '56rem' }}>
                This page reports what CARES can resolve from authenticated runtime sources today. Missing tenant policy inputs stay
                conservative and do not become permission claims.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Link to="/my-schedule/requests" className="btn btn-primary btn-wave">
                Back to My Requests
              </Link>
              <Link to="/my-schedule/calendar" className="btn btn-light btn-wave">
                Open My Schedule
              </Link>
            </div>
          </div>
        </Card.Body>
      </Card>

      {loading ? (
        <Card className="custom-card">
          <Card.Body className="d-flex align-items-center gap-2 text-muted">
            <Spinner animation="border" size="sm" />
            Resolving request policy.
          </Card.Body>
        </Card>
      ) : error ? (
        <Alert variant="danger" className="custom-card">
          {error}
        </Alert>
      ) : policy ? (
        <>
          <Alert variant={policy.dataCompleteness.status === 'unavailable' ? 'danger' : 'light'} className="custom-card border">
            {policy.dataCompleteness.message}
          </Alert>

          <Row className="g-4">
            <Col xl={4} md={6}>
              <Card className="custom-card h-100">
                <Card.Body>
                  <p className="text-muted text-uppercase fs-12 fw-semibold mb-2">Resolved scope</p>
                  <div className="fs-4 fw-semibold mb-2">{selectedGroupLabel}</div>
                  <p className="text-muted mb-0">
                    Scope type: {policy.resolvedScope?.scopeType ?? 'Unavailable'}
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col xl={4} md={6}>
              <Card className="custom-card h-100">
                <Card.Body>
                  <p className="text-muted text-uppercase fs-12 fw-semibold mb-2">Policy completeness</p>
                  <div className={`text-${completenessTone[policy.dataCompleteness.status]} fs-4 fw-semibold mb-2`}>
                    {policy.dataCompleteness.status}
                  </div>
                  <p className="text-muted mb-0">
                    Fallback source: {policy.policy.fallbackSource ?? 'None'}
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col xl={4} md={12}>
              <Card className="custom-card h-100">
                <Card.Body>
                  <p className="text-muted text-uppercase fs-12 fw-semibold mb-2">Runtime sources</p>
                  <div className="fs-6 fw-semibold mb-2">{policy.policy.provenance}</div>
                  <p className="text-muted mb-0">
                    Resolved {new Date(policy.policy.resolvedAt).toLocaleString()}.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {policy.availableGroups.length > 1 ? (
            <Card className="custom-card">
              <Card.Body>
                <Form.Label>View request policy for department</Form.Label>
                <Form.Select
                  value={selectedGroup}
                  onChange={(event) => {
                    const nextGroup = event.target.value;
                    setSelectedGroup(nextGroup);
                    void loadPolicy(nextGroup);
                  }}
                >
                  {policy.availableGroups.map((group) => (
                    <option key={group.id} value={group.slug}>
                      {group.name} ({group.company.slug ?? 'company'})
                    </option>
                  ))}
                </Form.Select>
              </Card.Body>
            </Card>
          ) : null}

          <Row className="g-4">
            {actionLabels.map((actionLabel) => (
              <Col xl={4} md={6} key={actionLabel.key}>
                <ActionPermissionCard label={actionLabel.label} action={policy.actionPermissions[actionLabel.key]} />
              </Col>
            ))}
          </Row>

          <Row className="g-4">
            <Col xl={7}>
              <PolicySectionCard
                title="Current effective workflow behavior"
                items={policy.currentPolicy}
                emptyMessage="No current workflow policy details are available for this account."
              />
            </Col>
            <Col xl={5}>
              <PolicySectionCard
                title="Future or unpublished policy inputs"
                items={policy.futurePolicy}
                emptyMessage="No future-dated request policy has been published."
              />
            </Col>
          </Row>

          <Card className="custom-card">
            <Card.Header>
              <Card.Title>Source coverage</Card.Title>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={6}>
                  <h6 className="mb-2">Available runtime sources</h6>
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                    {policy.dataCompleteness.availableSources.map((source) => (
                      <li key={source} className="d-flex gap-2">
                        <i className="ri-checkbox-circle-line text-success fs-18" />
                        <span>{source}</span>
                      </li>
                    ))}
                  </ul>
                </Col>
                <Col md={6}>
                  <h6 className="mb-2">Missing policy sources</h6>
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                    {policy.dataCompleteness.missingSources.map((source) => (
                      <li key={source} className="d-flex gap-2">
                        <i className="ri-error-warning-line text-warning fs-18" />
                        <span>{source}</span>
                      </li>
                    ))}
                  </ul>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </>
      ) : null}
    </>
  );
};

export default RequestPolicyPage;
